import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import type { RequestType } from '../../../shared/api/types'
import type { IntakeMeta } from '../../../shared/api/requests.api'
import type { ItemRow } from '../components/ItemsTable'

const DEBOUNCE_MS = 2000

interface Params {
  clientId: string
  type: RequestType
  sellerEntityId: string
  deadline: string
  notes: string
  items: ItemRow[]
  /** Контекст распознавания — rawText + parsedLines. Учитывается в fingerprint,
   *  чтобы автосейв триггерился при правке текста вставки. */
  intakeMeta: IntakeMeta | null
  /** id из URL ?draftId=… — если есть, patch-аем его, а не создаём новый. */
  urlDraftId: string | null
  /** Дожидаемся пока форма заинициализирована (иначе автосейв перезапишет черновик пустотой). */
  enabled: boolean
  /** Реальное сохранение — из useNewRequestSubmit.autoSave. */
  save: () => Promise<string | null>
  /** Отметить черновик как «только что инициализирован» — чтобы исходная загрузка из БД
   *  не триггерила автосейв. */
  onSaved: (draftId: string) => void
}

/**
 * Автосохранение новой заявки в черновик.
 *
 * Как работает:
 *  - Следит за изменениями полей формы (clientId, type, sellerEntityId, deadline, notes, items).
 *  - Debounce 2 сек — сохраняем только после паузы в редактировании, чтобы не спамить API.
 *  - Первое сохранение создаёт DRAFT и обновляет URL на ?draftId=…, последующие patch-ят его.
 *  - Без клиента (clientId) не сохраняем: сервер требует обязательный FK.
 *  - Без enabled=true не сохраняем: при открытии существующего черновика форма инициализируется
 *    из БД, и первый же рендер с «изменениями» триггерил бы автосейв ещё до того, как
 *    пользователь что-то поправил.
 *
 * beforeunload специально НЕ делаем: sendBeacon для multipart-flow (POST + bulk items) не
 * работает нативно. 2-секундный debounce закрывает 99% сценариев — человек думает между
 * правками дольше.
 */
export function useAutoSaveDraft({
  clientId,
  type,
  sellerEntityId,
  deadline,
  notes,
  items,
  intakeMeta,
  urlDraftId,
  enabled,
  save,
  onSaved,
}: Params) {
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [, setSearchParams] = useSearchParams()
  const queryClient = useQueryClient()

  // Снимок данных формы под последним срабатыванием таймера — для guard против
  // «сохранили стейл» при быстрых правках.
  const latest = useRef({ clientId, type, sellerEntityId, deadline, notes, items, intakeMeta })
  latest.current = { clientId, type, sellerEntityId, deadline, notes, items, intakeMeta }

  // Чтобы не сохранять одно и то же дважды — сравниваем сериализованный «отпечаток».
  const lastFingerprintRef = useRef<string | null>(null)
  const inFlightRef = useRef(false)

  useEffect(() => {
    if (!enabled) return
    if (!clientId) return
    const fingerprint = JSON.stringify({
      clientId,
      type,
      sellerEntityId,
      deadline,
      notes,
      items: items.map((i) => ({
        pn: i.partNumber,
        b: i.brand,
        q: i.quantity,
        u: i.unit,
        n: i.note,
        p: i.priority,
        m: i.matrix,
        c: i.checked,
      })),
      intakeMeta: intakeMeta
        ? { rt: intakeMeta.rawText ?? '', pl: intakeMeta.parsedLines?.length ?? 0 }
        : null,
    })
    if (fingerprint === lastFingerprintRef.current) return

    const timer = setTimeout(async () => {
      if (inFlightRef.current) return
      inFlightRef.current = true
      setIsSaving(true)
      try {
        const id = await save()
        if (id) {
          lastFingerprintRef.current = fingerprint
          setLastSavedAt(new Date())
          // Инвалидируем кэш draft'а в background, чтобы следующий autoSave
          // увидел актуальный список items.id. Раньше тут был removeQueries —
          // оно удаляло данные мгновенно, и при быстрой второй правке
          // autoSave получал draftRequest=undefined и уходил в ветку create,
          // порождая дубликаты черновика. invalidate оставляет данные в
          // кэше и только помечает их stale.
          void queryClient.invalidateQueries({ queryKey: ['requests', id] })
          onSaved(id)
          if (!urlDraftId) {
            // Первый автосейв — зафиксируем id в URL, чтобы при случайной
            // перезагрузке не создавался второй черновик.
            setSearchParams(
              (prev) => {
                const p = new URLSearchParams(prev)
                p.set('draftId', id)
                return p
              },
              { replace: true },
            )
          }
        }
      } catch {
        // Автосейв не кричит в UI — молча пробуем ещё раз при следующем изменении.
      } finally {
        inFlightRef.current = false
        setIsSaving(false)
      }
    }, DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [
    enabled,
    clientId,
    type,
    sellerEntityId,
    deadline,
    notes,
    items,
    intakeMeta,
    save,
    urlDraftId,
    setSearchParams,
    queryClient,
    onSaved,
  ])

  return { lastSavedAt, isSaving }
}
